'use client';

import {
  Box,
  Button,
  Flex,
  Grid,
  Heading,
  HStack,
  Link,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react';
import { ArrowUpRight, RefreshCw } from 'lucide-react';
import NextLink from 'next/link';
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/app/auth-provider';
import { useProject } from '@/contexts/ProjectContext';
import { useBrandsPageDiscovery } from '@/hooks/useBrandsPageDiscovery';
import { buildDataOverviewRanges } from '@/lib/data-overview-ranges';
import { buildConfiguredBrandGroups, matchesBrandValue } from '@/utils/brandMatching';
import {
  buildBrandHomeTrendPoints,
  buildBrandHomeWidgetSummary,
} from '@/lib/workspace-home-brand-widget';

interface Performance {
  providers?: Record<string, { totals?: Record<string, unknown> }>;
  sourceCoverage?: Array<{
    provider?: string;
    display_name?: string;
    last_sync_at?: string;
    last_sync_status?: string;
  }>;
}
interface HealthSource {
  id: string;
  displayName?: string;
  provider: string;
  status: string;
  lastSyncAt?: string;
  lastSyncStatus?: string;
}
function sourceProblem(source: HealthSource): string | null {
  if (source.status !== 'connected') return 'Reconnect this source';
  if (source.lastSyncStatus === 'failed') return 'Last sync failed';
  if (!source.lastSyncAt || !Number.isFinite(Date.parse(source.lastSyncAt)))
    return 'Waiting for its first successful sync';
  if (!['success', 'started'].includes(source.lastSyncStatus || ''))
    return 'Sync could not be confirmed';
  return null;
}
const destinations = [
  {
    title: 'AI search',
    description: 'Visibility, rankings and how AI describes your brand.',
    href: '/app/visibility',
    links: [
      ['Rankings', '/app/rank'],
      ['Sentiment', '/app/sentiment'],
    ],
  },
  {
    title: 'Authority & citations',
    description: 'The sources and publishers shaping answers.',
    href: '/app/citation-report',
    links: [
      ['Source trends', '/app/citation-report/charts'],
      ['Publishers', '/app/channel-manager'],
      ['Page Tracker', '/app/publishers/tracker'],
    ],
  },
  {
    title: 'Website performance',
    description: 'Traffic, search demand and the pages that convert.',
    href: '/app/pages/overview',
    links: [
      ['Pages', '/app/pages'],
      ['Revenue', '/app/revenue'],
    ],
  },
];
function number(value: unknown, suffix = '') {
  if (value === null || value === undefined || value === '') return '—';
  const parsed = Number(value);
  return Number.isFinite(parsed)
    ? `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 1, notation: 'compact' }).format(parsed)}${suffix}`
    : '—';
}
function DashboardLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <Link
      as={NextLink}
      href={href}
      display="inline-flex"
      alignItems="center"
      gap="1.5"
      fontSize="xs"
      color="ui.text.secondary"
      textDecoration="none"
      _hover={{ color: 'ui.text.primary', textDecoration: 'underline' }}
    >
      {children}
      <ArrowUpRight size={13} />
    </Link>
  );
}
export function DataControlCenter() {
  const { user } = useAuth();
  const { currentProject, loading: projectLoading, isReady, isTransitioning } = useProject();
  const projectId = currentProject?.id;
  const ready = Boolean(user && projectId && isReady && !isTransitioning && !projectLoading);
  const [period, setPeriod] = useState(30);
  const [refresh, setRefresh] = useState(0);
  const { intelligence: intelligenceRange, website: range } = useMemo(
    () => buildDataOverviewRanges(period),
    [period, refresh]
  );
  const name = currentProject?.brand_settings?.brand_name || currentProject?.name || 'Your brand';
  const configured = useMemo(
    () =>
      buildConfiguredBrandGroups({
        brandName: name,
        projectBrandSlug: currentProject?.brand_settings?.brand_slug || name,
        brandVariations: currentProject?.brand_settings?.brand_variations || [],
        brandMergeGroups: currentProject?.brand_merge_groups || [],
      }),
    [name, currentProject]
  );
  const groups = useMemo(
    () => [
      { canonical_name: name, variations: currentProject?.brand_settings?.brand_variations || [] },
      ...(currentProject?.brand_merge_groups || []),
    ],
    [name, currentProject]
  );
  const discovery = useBrandsPageDiscovery({
    enabled: ready,
    brand_groups: groups,
    start_date: intelligenceRange.startDate,
    end_date: intelligenceRange.endDateExclusive,
    limit: 200,
  });
  const match = configured.find(group => group.isProjectDefaultBrand)?.matchSet;
  const ownBrand = match
    ? discovery.brands.find(
        brand =>
          matchesBrandValue(brand.brand, match) || matchesBrandValue(brand.brand_lowercase, match)
      )
    : undefined;
  const trends = match
    ? discovery.timeSeries.filter(
        row => matchesBrandValue(row.brand, match) || matchesBrandValue(row.brand_lowercase, match)
      )
    : [];
  const brandSummary = ownBrand
    ? buildBrandHomeWidgetSummary(ownBrand, buildBrandHomeTrendPoints(trends))
    : null;
  const [webState, setWebState] = useState<{ key: string; data?: Performance; error?: string }>({
    key: '',
  });
  const key = `${user?.uid}:${projectId}:${range.startDate}:${range.endDate}:${refresh}`;
  useEffect(() => {
    if (!ready || !user) return;
    const controller = new AbortController();
    setWebState({ key });
    void (async () => {
      try {
        const token = await user.getIdToken();
        const response = await fetch('/api/attune/website/performance', {
          method: 'POST',
          signal: controller.signal,
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ projectId, ...range, limit: 5 }),
        });
        if (!response.ok) throw new Error('Website data could not be loaded.');
        const data: Performance = await response.json();
        if (!controller.signal.aborted) setWebState({ key, data });
      } catch {
        if (!controller.signal.aborted)
          setWebState({ key, error: 'Website data could not be loaded.' });
      }
    })();
    return () => controller.abort();
  }, [ready, user, projectId, key, range]);
  const healthKey = `${user?.uid}:${projectId}:${refresh}`;
  const [healthState, setHealthState] = useState<{
    key: string;
    sources?: HealthSource[];
    error?: boolean;
  }>({ key: '' });
  useEffect(() => {
    if (!ready || !user) return;
    const controller = new AbortController();
    setHealthState({ key: healthKey });
    void (async () => {
      try {
        const token = await user.getIdToken();
        const response = await fetch(
          `/api/integrations/analytics/status?projectId=${encodeURIComponent(projectId!)}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
          }
        );
        if (!response.ok) throw new Error('Health check failed');
        const payload = await response.json();
        if (!Array.isArray(payload.sources)) throw new Error('Invalid source status');
        if (!controller.signal.aborted)
          setHealthState({ key: healthKey, sources: payload.sources });
      } catch {
        if (!controller.signal.aborted) setHealthState({ key: healthKey, error: true });
      }
    })();
    return () => controller.abort();
  }, [ready, user, projectId, healthKey]);
  const health = ready && healthState.key === healthKey ? healthState : undefined;
  const problems = (health?.sources || [])
    .map(source => ({ source, reason: sourceProblem(source) }))
    .filter(item => item.reason);
  const healthLoaded = Boolean(health?.sources || health?.error);
  const healthy = Boolean(health?.sources?.length && !problems.length);
  const web = ready && webState.key === key ? webState.data : undefined;
  const webError = webState.key === key ? webState.error : undefined;
  const ga4 = web?.providers?.ga4?.totals;
  const gsc = web?.providers?.gsc?.totals;
  const sources = web?.sourceCoverage || [];
  const loading = !ready || discovery.loading;
  const metrics = [
    {
      title: 'AI visibility',
      value: loading
        ? '…'
        : !discovery.error &&
            brandSummary &&
            (trends.length > 0 || ownBrand?.discovery_visibility != null)
          ? number(brandSummary.visibilityPct, '%')
          : '—',
      note: 'Latest sampled visibility in this period',
      href: '/app/visibility',
    },
    {
      title: 'Top-3 share of voice',
      value: loading
        ? '…'
        : !discovery.error &&
            brandSummary &&
            (trends.length > 0 || ownBrand?.top3_share_pct != null)
          ? number(brandSummary.top3SovPct, '%')
          : '—',
      note: 'Latest top-3 share in this period',
      href: '/app/rank',
    },
    {
      title: 'Website sessions',
      value: !web && !webError ? '…' : number(ga4?.sessions),
      note: 'Google Analytics · period total',
      href: '/app/pages/analytics/ga4',
    },
    {
      title: 'Organic search clicks',
      value: !web && !webError ? '…' : number(gsc?.search_clicks),
      note: 'Search Console · period total',
      href: '/app/pages/analytics/gsc',
    },
    ...(ga4?.revenue !== null &&
    ga4?.revenue !== undefined &&
    ga4?.revenue !== '' &&
    Number.isFinite(Number(ga4.revenue))
      ? [
          {
            title: 'Revenue',
            value: number(ga4.revenue),
            note: 'GA4 reported amount · period total',
            href: '/app/pages/analytics/ga4',
          },
        ]
      : []),
  ];
  if (!projectLoading && !currentProject)
    return (
      <Box p="8">
        <Heading size="lg">Choose a project</Heading>
        <Text mt="2" color="ui.text.secondary">
          Select a project to see its data overview.
        </Text>
      </Box>
    );
  return (
    <Box
      h="full"
      overflowY="auto"
      px={{ base: '5', lg: '9' }}
      py={{ base: '6', lg: '8' }}
      data-section="dataControlCenter"
    >
      <Box maxW="1440px" mx="auto">
        <Flex justify="space-between" align="start" gap="4" flexWrap="wrap" mb="7">
          <Box>
            <Text fontSize="xs" color="ui.text.tertiary" mb="2">
              {name}
            </Text>
            <Heading
              as="h1"
              fontSize={{ base: '26px', md: '30px' }}
              fontWeight="600"
              letterSpacing="-0.035em"
            >
              Data overview
            </Heading>
            <Text mt="2" fontSize="sm" color="ui.text.secondary">
              Your market position, acquisition performance and data health.
            </Text>
          </Box>
          <VStack align="end" gap="2">
            <HStack gap="1" bg="ui.surface.input" borderRadius="lg" p="1">
              {[7, 30, 90].map(days => (
                <Button
                  key={days}
                  size="xs"
                  variant="ghost"
                  aria-pressed={period === days}
                  bg={period === days ? 'ui.surface.card' : 'transparent'}
                  onClick={() => setPeriod(days)}
                >
                  {days} days
                </Button>
              ))}
              <Button
                size="xs"
                variant="ghost"
                aria-label="Refresh overview"
                onClick={() => {
                  setRefresh(value => value + 1);
                  void discovery.refresh();
                }}
              >
                <RefreshCw size={14} />
              </Button>
            </HStack>
            <Text fontSize="11px" color="ui.text.tertiary">
              {intelligenceRange.startDate} – {intelligenceRange.endDate} · UTC
            </Text>
          </VStack>
        </Flex>
        {(discovery.error || webError) && (
          <Flex
            role="status"
            mb="5"
            gap="4"
            flexWrap="wrap"
            fontSize="sm"
            color="ui.text.secondary"
          >
            <Text>
              {discovery.error ? 'AI search data is unavailable.' : ''} {webError}
            </Text>
            <Button
              size="xs"
              variant="plain"
              onClick={() => {
                setRefresh(value => value + 1);
                void discovery.refresh();
              }}
            >
              Retry
            </Button>
          </Flex>
        )}
        <SimpleGrid
          columns={{ base: 1, sm: 2, xl: metrics.length }}
          gap="0"
          borderYWidth="1px"
          borderColor="ui.border.subtle"
          mb="8"
        >
          {metrics.map(metric => (
            <Link
              as={NextLink}
              href={metric.href}
              key={metric.title}
              display="block"
              p="5"
              textDecoration="none"
              _hover={{ bg: 'ui.surface.rowHover' }}
            >
              <HStack justify="space-between">
                <Text fontSize="xs" color="ui.text.secondary">
                  {metric.title}
                </Text>
                <ArrowUpRight size={14} />
              </HStack>
              <Text
                fontSize="36px"
                fontWeight="500"
                letterSpacing="-0.04em"
                lineHeight="1.2"
                my="3"
              >
                {metric.value}
              </Text>
              <Text fontSize="11px" color="ui.text.tertiary">
                {metric.note}
              </Text>
            </Link>
          ))}
        </SimpleGrid>
        <Grid
          templateColumns={{ base: '1fr', xl: 'minmax(0, 1.15fr) minmax(0, 1fr)' }}
          gap={{ base: '7', xl: '10' }}
          mb="9"
        >
          <Box minW={0}>
            <HStack justify="space-between" mb="4">
              <Heading size="md" fontWeight="600">
                Market position
              </Heading>
              <DashboardLink href="/app/ai-search/competitors">Market leaders</DashboardLink>
            </HStack>
            <Text fontSize="xs" color="ui.text.tertiary" mb="4">
              Share of voice among brands returned for this period.
            </Text>
            <HStack px="3" pb="2" justify="space-between" fontSize="11px" color="ui.text.tertiary">
              <Text>Brand</Text>
              <Text>Share of voice</Text>
            </HStack>
            {discovery.loading ? (
              <Text p="3" fontSize="sm" color="ui.text.tertiary">
                Loading market data…
              </Text>
            ) : discovery.brands.length ? (
              [...discovery.brands]
                .sort((a, b) => Number(b.visibility_pct ?? -1) - Number(a.visibility_pct ?? -1))
                .slice(0, 5)
                .map(brand => {
                  const value = brand.visibility_pct;
                  return (
                    <Flex
                      key={brand.brand_lowercase || brand.brand}
                      align="center"
                      justify="space-between"
                      gap="5"
                      px="3"
                      py="3"
                      borderTopWidth="1px"
                      borderColor="ui.border.subtle"
                      bg={brand === ownBrand ? 'ui.surface.selected' : 'transparent'}
                    >
                      <Text fontSize="sm" fontWeight={brand === ownBrand ? '600' : '400'} truncate>
                        {brand.brand}
                        {brand === ownBrand ? ' · You' : ''}
                      </Text>
                      <HStack gap="3">
                        <Box w="72px" h="3px" bg="ui.surface.input" borderRadius="full">
                          <Box
                            h="full"
                            bg="ui.text.tertiary"
                            borderRadius="full"
                            w={`${Math.max(0, Math.min(100, value || 0))}%`}
                          />
                        </Box>
                        <Text fontSize="sm" w="48px" textAlign="right">
                          {number(value, '%')}
                        </Text>
                      </HStack>
                    </Flex>
                  );
                })
            ) : (
              <Text py="5" fontSize="sm" color="ui.text.secondary">
                {discovery.error
                  ? 'Market data could not be loaded.'
                  : 'No sampled brand data for this period.'}
              </Text>
            )}
          </Box>
          <Box>
            <HStack justify="space-between" mb="4">
              <Heading size="md" fontWeight="600">
                Acquisition
              </Heading>
              <DashboardLink href="/app/pages/overview">Website dashboard</DashboardLink>
            </HStack>
            <Text fontSize="xs" color="ui.text.tertiary" mb="4">
              Provider totals for {range.startDate} – {range.endDate} · UTC.
            </Text>
            {[
              {
                title: 'Google Analytics',
                href: '/app/pages/analytics/ga4',
                values: [
                  ['Sessions', ga4?.sessions],
                  ['Conversions', ga4?.conversions],
                ],
              },
              {
                title: 'Search Console',
                href: '/app/pages/analytics/gsc',
                values: [
                  ['Impressions', gsc?.search_impressions],
                  ['Clicks', gsc?.search_clicks],
                ],
              },
            ].map(provider => (
              <Box key={provider.title} borderTopWidth="1px" borderColor="ui.border.subtle" py="4">
                <DashboardLink href={provider.href}>{provider.title}</DashboardLink>
                <SimpleGrid columns={2} gap="4" mt="3">
                  {provider.values.map(([label, value]) => (
                    <Box key={String(label)}>
                      <Text fontSize="23px" letterSpacing="-0.025em">
                        {!web && !webError ? '…' : number(value)}
                      </Text>
                      <Text fontSize="xs" color="ui.text.tertiary">
                        {String(label)}
                      </Text>
                    </Box>
                  ))}
                </SimpleGrid>
              </Box>
            ))}
            {!web && !webError ? (
              <Text fontSize="xs" color="ui.text.tertiary">
                Loading connected analytics…
              </Text>
            ) : !sources.length && !webError ? (
              <DashboardLink href="/app/workspace?mode=integrations">
                Connect a data source
              </DashboardLink>
            ) : null}
          </Box>
        </Grid>
        <Box borderTopWidth="1px" borderColor="ui.border.subtle" pt="6" mb="8">
          <HStack justify="space-between" mb="4">
            <Heading size="md" fontWeight="600">
              Data health
            </Heading>
            <DashboardLink href="/app/workspace?mode=integrations">Manage sources</DashboardLink>
          </HStack>
          <HStack gap="2.5" role="status">
            <Box
              boxSize="2"
              borderRadius="full"
              bg={
                !healthLoaded
                  ? 'ui.text.tertiary'
                  : healthy
                    ? 'status.success.fg'
                    : 'status.warning.fg'
              }
            />
            <Text
              fontSize="sm"
              fontWeight="600"
              color={
                !healthLoaded
                  ? 'ui.text.secondary'
                  : healthy
                    ? 'status.success.fg'
                    : 'status.warning.fg'
              }
            >
              {!healthLoaded ? 'Checking…' : healthy ? 'Healthy' : 'Needs attention'}
            </Text>
            {healthy ? (
              <Text fontSize="sm" color="ui.text.secondary">
                All connected sources synced successfully.
              </Text>
            ) : null}
          </HStack>
          {healthLoaded && !healthy ? (
            <VStack align="stretch" gap="1" mt="2" pl="4.5">
              {health?.error ? (
                <Text fontSize="sm" color="ui.text.secondary">
                  Couldn’t check source health. Refresh to try again.
                </Text>
              ) : !health?.sources?.length ? (
                <Text fontSize="sm" color="ui.text.secondary">
                  Connect a source to start collecting data.
                </Text>
              ) : (
                problems.map(({ source, reason }) => (
                  <Text key={source.id} fontSize="sm" color="ui.text.secondary">
                    {source.displayName || source.provider}: {reason}.
                  </Text>
                ))
              )}
            </VStack>
          ) : null}
        </Box>
        <SimpleGrid columns={{ base: 1, lg: 3 }} gap="5">
          {destinations.map(destination => (
            <Box key={destination.title} borderTopWidth="2px" borderColor="ui.border.subtle" pt="4">
              <DashboardLink href={destination.href}>
                <Text as="span" fontSize="sm" fontWeight="600" color="ui.text.primary">
                  {destination.title}
                </Text>
              </DashboardLink>
              <Text fontSize="xs" color="ui.text.secondary" mt="2" mb="4">
                {destination.description}
              </Text>
              <HStack gap="4" flexWrap="wrap">
                {destination.links.map(([label, href]) => (
                  <DashboardLink key={href} href={href}>
                    {label}
                  </DashboardLink>
                ))}
              </HStack>
            </Box>
          ))}
        </SimpleGrid>
      </Box>
    </Box>
  );
}
